import { FC, useEffect, useMemo, useState, useRef } from "react";

import { useLocation } from "react-router-dom";
import { Table } from "antd";

import { useScreen } from "common/shared-hooks";
import { useFullInvestments } from "common/shared-hooks/useFullInvestments";
import { useAltQuotesSelector } from "domains/altQuotes";
import { useLiquiditySelector } from "domains/liquidity/store/liquidity.selectors";
import { currencyFormatter } from "domains/liquidity/shared/utils/inputValueFormatter";
import { assetTypes } from "v2/features/assets/addAsset/SelectOtherAsset/constants";
import { setSelectedAssetsData } from "domains/liquidity/store/liquidity.reducer";
import { useAppDispatch } from "store/store.hooks";
import { TAssetType } from "domains/liquidity/shared/types";

export type TValidAssetsState = {
  validSelectedAssets: any[];
  validSelectedAssetsData: TAssetType[];
};

export const mobileFundColumns = [
    {
      title: "Fund Manager & Fund Name",
      dataIndex: "fund",
      key: "fund",
      className: "ben-w-70",
    },
    {
      title: "",
      key: "action",
      dataIndex: "action",
      align: "right",
      className: "ben-w-30",
    } as const,
];
  
export const desktopFundColumns = [
  {
    title: "Fund Manager",
    dataIndex: "fundManager",
    key: "fundManager",
    className: "ben-w-30",
  },
  {
    title: "Fund Name",
    dataIndex: "fundName",
    key: "fundName",
    className: "ben-w-30",
  },
  {
    title: "NAV",
    dataIndex: "nav",
    key: "nav",
    className: "ben-w-20",
  },
  {
    title: "",
    key: "action",
    dataIndex: "action",
    align: "right",
    className: "ben-w-20",
  } as const,
];

export const altQuoteDesktopFundColumns = [
  {
    title: "Fund Manager",
    dataIndex: "fundManager",
    key: "fundManager",
    className: "ben-w-30",
  },
  {
    title: "Fund Name",
    dataIndex: "fundName",
    key: "fundName",
    className: "ben-w-35",
  },
  {
    title: "NAV",
    dataIndex: "nav",
    key: "nav",
    className: "ben-w-25",
  },
  {
    title: "Unfunded Commitments",
    dataIndex: "unfundedCommitments",
    key: "unfundedCommitments",
    className: "ben-w-25 ben-white-space-break-spaces",
  },
];

const SelectAssetsForLiquidityRequest: FC<any> = ({ _selectedAssets, setSelectedAssets, investments }) => {
    const dispatch = useAppDispatch();
    const { mobile } = useScreen();
    const { isAltQuoteTab } = useAltQuotesSelector();
    const { selectedAssetsForLiquidity } = useLiquiditySelector();
    const {
        getAssetsWithFundManager,
        isInvestmentInfoCompleted,
        navigateToEditAsset,
        setInvestmentInfoCompleted,
    } = useFullInvestments();
    const { search, pathname } = useLocation();
    
    const isLiquidityRequest = pathname.includes('new-liquidity-request');
    
    const queryParams = new URLSearchParams(search);
    const confirmAssets = queryParams.get('confirmAssets') === "true"

    const desktopColumns = isAltQuoteTab ? altQuoteDesktopFundColumns : desktopFundColumns;
    const mobileColumns = isAltQuoteTab
      ? mobileFundColumns.filter((column) => column.key !== "action")
      : mobileFundColumns;
    const columns = mobile ? mobileColumns : desktopColumns;

    const [ initialValidAssets, setInitialValidAssets ] = useState<TValidAssetsState>({
      validSelectedAssets: [],
      validSelectedAssetsData: [],
    });

    const allRowsSelectedRef = useRef(true);
    const hasInitializedRef = useRef(false);
    const userHasInteractedRef = useRef(false);

    useEffect(() => {
        if (
          !hasInitializedRef.current &&
          Array.isArray(selectedAssetsForLiquidity) && selectedAssetsForLiquidity.length > 0 &&
          Array.isArray(investments) && investments.length > 0
        ) {
          const validData: TAssetType[] = [];
          const validAssets = selectedAssetsForLiquidity.filter((assetId: any) => {
            const investment = investments.find((inv: any) => inv.id === assetId);
            if (investment) {
              if (isLiquidityRequest) {
                validData.push({ ...investment, is_investment_info_complete: true });
                return true;
              } else if (isInvestmentInfoCompleted(investment)) {
                validData.push({ ...investment, is_investment_info_complete: true });
                return true;
              }
            }
            return false;
          });
          setInitialValidAssets({ validSelectedAssets: validAssets, validSelectedAssetsData: validData });
          dispatch(setSelectedAssetsData(validData));
          hasInitializedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [ selectedAssetsForLiquidity, investments ]);

    const { validSelectedAssets, validSelectedAssetsData } = initialValidAssets;

    const validDataKey = useMemo(() => {
      return JSON.stringify(validSelectedAssetsData.map(item => item.id).sort());
    }, [ validSelectedAssetsData ]);

    const rowSelection: any = {
        selectedRowKeys: validSelectedAssets,
        onSelect: (record: any, selected: boolean, selectedRows: any[]) => {
            userHasInteractedRef.current = true;
            const investment = investments.find((investment: any) => investment.id === record.key);
            if (selected && !isLiquidityRequest && !isInvestmentInfoCompleted(investment)) {
                navigateToEditAsset(investment.asset_id);
                return;
            }
            const newSelectedKeys = selected 
                ? [ ...validSelectedAssets, record.key ]
                : validSelectedAssets.filter(key => key !== record.key);
            const selectedInvestments = newSelectedKeys.map(
                (key) => investments.find((investment: any) => investment.id === key),
            );

            if (newSelectedKeys.length === 0) {
              allRowsSelectedRef.current = !allRowsSelectedRef.current;
            }

            dispatch(setSelectedAssetsData(setInvestmentInfoCompleted(selectedInvestments)));
            setSelectedAssets(newSelectedKeys);
            setInitialValidAssets({
                validSelectedAssets: newSelectedKeys,
                validSelectedAssetsData: setInvestmentInfoCompleted(selectedInvestments),
            });
        },
        onSelectAll: (selected: boolean, selectedRows: any[], _changeRows: any[]) => {
            userHasInteractedRef.current = true;
            const newSelectedValue = !allRowsSelectedRef.current;
            allRowsSelectedRef.current = newSelectedValue;
            
            let newSelectedKeys = newSelectedValue ? selectedRows.map((row: any) => row.key) : [];
            let selectedInvestments = newSelectedKeys.map(
                (key) => investments.find((investment: any) => investment.id === key),
            );
            if (!isLiquidityRequest && newSelectedValue) {
                newSelectedKeys = [];
                selectedInvestments = selectedInvestments.filter(
                    (investment: any) => {
                        const shouldBeSelected = isInvestmentInfoCompleted(investment);
                        if (shouldBeSelected) {
                            newSelectedKeys.push(investment.id); 
                        }
                        return shouldBeSelected;
                    },
                );
            }

            setSelectedAssets(newSelectedKeys);
            dispatch(setSelectedAssetsData(setInvestmentInfoCompleted(selectedInvestments)));
            setInitialValidAssets({
                validSelectedAssets: newSelectedKeys,
                validSelectedAssetsData: setInvestmentInfoCompleted(selectedInvestments),
            });
        },
    };

    const getAssetTypeName = (asset:any) => {
        if (asset.asset_type === 5) {
          const description = asset.asset_description?.length > 80 
            ? asset.asset_description.substring(0, 80) 
            : asset.asset_description;
          return description;
        }
        if (asset.asset_type === 2) {
          const description = asset.sponsor_description.length > 80 
            ? asset.sponsor_description.substring(0, 80) 
            : asset.sponsor_description;
          return description;
        }
        
        return assetTypes[ asset.asset_type ].title;
    };
    
    const getSponsorName = (asset:any) => {
        if (asset.asset_type === 2) {
          const description = asset.sponsor?.length > 80 
            ? asset.sponsor.substring(0, 80) 
            : asset.sponsor;
          return description;
        }
    };

    const fullAssets = getAssetsWithFundManager(investments);
  
    const fundDataSource = [ ...fullAssets ]?.reverse().map((asset: any) => ({
        key: asset.id,
        fund: (
            <div>
                <b>
                    <span>{asset.asset_name || asset.fund_name || getAssetTypeName(asset)}</span>
                </b>
                <br />
                <span>{asset.fund_manager || asset.sponsor || getSponsorName(asset)}</span>
                <br />
                <span className="ben-fund-assets-text">NAV: </span>
                {currencyFormatter(asset.current_net_asset_value)}
                {isAltQuoteTab && (
                    <>
                        <br />
                        <span className="ben-fund-assets-text">Unfunded Commitments: </span>
                        {currencyFormatter(asset.unfunded_commitment_currency)}
                    </>
                )}
            </div>
        ),
        fundManager: asset.fund_manager || asset.sponsor ,
        fundName: asset.asset_name || asset.fund_name,
        nav: currencyFormatter(asset.current_net_asset_value),
        unfundedCommitments: currencyFormatter(asset.unfunded_commitment_currency),
    }));

    useEffect(() => {
      if (!confirmAssets && !selectedAssetsForLiquidity.length && fullAssets?.length && !userHasInteractedRef.current) {
        setSelectedAssets(fullAssets.map(({ id }: any) => id))
      }
    }, [ confirmAssets, selectedAssetsForLiquidity.length, fullAssets?.length ])
  
    if (!fullAssets?.length) return null;       
  
    return (
      <Table
        style={ {
          maxHeight: mobile ? "calc(100vh - 400px)" : `calc(100vh - 520px)`,
          minHeight: "170px",
          overflow: "hidden",
          overflowY: "auto",
          flex: 1,
        } }
        pagination={ false }
        columns={ columns }
        dataSource={ fundDataSource }
        rowSelection={ rowSelection }
        className="ben-fund-assets-table"
      />
    );
}

export default SelectAssetsForLiquidityRequest;
